const trim = (value?: string): string | undefined => {
  const next = value?.trim();
  return next || undefined;
};

const required = (value: string | undefined, name: string): string => {
  if (!value) {
    throw new Error(`Missing required env var: ${name}. Copy .env.example to .env and fill it in.`);
  }
  return value;
};

/** Production backend — the only sane destination for a portal built for production. */
const PRODUCTION_API_BASE_URL = 'https://api.mediclynk.com/api/v1';
/** Local dev default: backend/.env sets PORT=3000, and 3001 is the alternate local port. */
const DEVELOPMENT_API_BASE_URL = 'http://localhost:3001/api/v1';

/**
 * Backend this portal administers.
 *
 * The fallback used to be the localhost URL unconditionally, so a production
 * build whose `VITE_API_BASE_URL` failed to reach the bundler — an unset CI
 * secret, a typo in the env key, a build run outside the workflow — shipped a
 * portal that quietly pointed every admin request at `http://localhost:3001`.
 * In a deployed page that is not a wrong-backend bug with a wrong answer; it is
 * a portal where nothing loads at all, and nothing in the build says why.
 *
 * Production therefore falls back to production. Only a dev build keeps the
 * localhost default, where it's what you actually want.
 */
function resolveApiBaseUrl(): string {
  const configured = trim(import.meta.env.VITE_API_BASE_URL);
  if (configured) return configured;
  return import.meta.env.PROD ? PRODUCTION_API_BASE_URL : DEVELOPMENT_API_BASE_URL;
}

export const env = {
  firebaseApiKey: required(trim(import.meta.env.VITE_FIREBASE_API_KEY), 'VITE_FIREBASE_API_KEY'),
  firebaseAuthDomain: required(
    trim(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    'VITE_FIREBASE_AUTH_DOMAIN',
  ),
  firebaseProjectId: required(
    trim(import.meta.env.VITE_FIREBASE_PROJECT_ID),
    'VITE_FIREBASE_PROJECT_ID',
  ),
  firebaseStorageBucket: trim(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
  firebaseMessagingSenderId: trim(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
  firebaseAppId: required(trim(import.meta.env.VITE_FIREBASE_APP_ID), 'VITE_FIREBASE_APP_ID'),
  firebaseMeasurementId: trim(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID),
  apiBaseUrl: resolveApiBaseUrl(),
  // Named per-stage Firestore database (must match the backend's FIRESTORE_DATABASE_ID, same as
  // app/services/firestoreDb.ts). Unset = "(default)", which is production's database.
  firestoreDatabaseId: trim(import.meta.env.VITE_FIRESTORE_DATABASE_ID),
  // Local-only: talk to `firebase emulators:start` instead of real Firebase Auth. Mirrors
  // app/services/firebaseAuth.ts — projectId must match the "demo-mediclynk" namespace the
  // backend's Firebase Admin SDK is initialized against (see backend/.env), otherwise the
  // backend's verifyIdToken() rejects the portal's tokens on audience mismatch.
  useFirebaseAuthEmulator: trim(import.meta.env.VITE_USE_FIREBASE_EMULATOR) === 'true',
  firebaseAuthEmulatorHost: trim(import.meta.env.VITE_FIREBASE_EMULATOR_HOST) ?? 'localhost',
  // Optional — App Check runs disabled (graceful no-op) until this is set. See
  // src/lib/appCheck.ts and .env.example for the rollout steps (mirrors
  // app/services/appCheck.ts's native-app rollout).
  recaptchaV3SiteKey: trim(import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY),
  // Microsoft SSO (ADM-32) — same Azure AD app registration the mobile app already uses
  // (app/services/publicConfig.ts's microsoftClientId), so the backend's
  // exchangeMicrosoftToken() `audience: clientId` check accepts tokens from either client.
  // Optional — the "Sign in with Microsoft" button hides itself when unset. See
  // .env.example for the Azure AD redirect-URI step required before this works.
  microsoftClientId: trim(import.meta.env.VITE_MICROSOFT_CLIENT_ID),
  // Google SSO (ADM-33) — the same OAuth 2.0 Web client Firebase auto-created for this
  // project (visible as the aud claim on any Firebase-issued Google sign-in). Required —
  // Google sign-in has no graceful-hide fallback since it's the portal's primary SSO path
  // (unlike Microsoft, which is opt-in). See src/lib/googleIdentity.ts and .env.example.
  googleClientId: trim(import.meta.env.VITE_GOOGLE_CLIENT_ID),
};
